import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProjects, useDeleteProject, useRestoreProject, useProjectCategoryOptions, useUpdateProject, useProjectListColors } from '@/hooks';
import {
  evaluateProjectColors,
  rowTreatmentClasses,
} from '@/lib/projectListColors';
import { ProjectColorDot, ProjectColorBadge } from '@/components/projects/ProjectColorIndicators';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { Button } from '@/components/ui/button';
import { ViewTabs, ViewTab } from '@/components/ui/view-tabs';
import { FilterBar } from '@/components/ui/filter-bar';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Trash2, Archive, RotateCcw, FileStack, AlertTriangle, Share2, FolderOpen, FileText } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { useFirmType } from '@/hooks/useFirmType';
import { cn } from '@/lib/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Project, WorkflowStage } from '@/lib/schemas';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useFeature } from '@/lib/features/FeatureProvider';
import { ProjectStageBadge } from '@/components/projects/ProjectStageBadge';
import { InlineStageSelect } from '@/components/projects/InlineStageSelect';
import { getTenantItem, setTenantItem, removeTenantItem, migrateLegacyKey } from '@/lib/tenantStorage';
import { api } from '@/lib/api';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useReferrerPage } from '@/hooks/useReferrerPage'; // 9.15c: Navigation/pagination fix
import { PaginationInfo } from '@/components/PaginationInfo';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { calculatePaginationValues } from '@/lib/pagination';
import { TemplateSelectionModal } from '@/components/TemplateSelectionModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProjectMobileCardList } from '@/components/projects/ProjectMobileCardList';

// View tabs configuration (Phase 7.5.13: Separate proposals from projects)
const projectViews: ViewTab[] = [
  { value: 'proposals', label: 'Proposals' },
  { value: 'active-work', label: 'Active Work' },
  { value: 'all', label: 'All Projects' },
  { value: 'action-needed', label: 'Action Needed' },
  { value: 'archived', label: 'Archived' },
];

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? '';
  // Read from tenant feature flags (canonical source) — NOT user.modules.
  // user.modules is per-user membership state and can drift from tenant flags,
  // producing silent 403s on the dual-gated GET /api/external-views call.
  // See docs/patterns/BACKEND_PATTERNS.md → "Feature Flag Gating".
  const hasPortalManagement = useFeature('portal_management');
  const firmType = useFirmType();
  // Issue #99: switch table -> card stack at < md so all key fields stay visible
  const isMobile = useIsMobile();

  // SPEC-project-list-colour-rules / R2: per-tenant colouring rules, applied
  // client-side. Inert by default — an empty rules array yields empty treatments
  // so the list renders exactly as today. The list renders normally while this
  // query loads (rules default to []).
  const { data: colorConfig } = useProjectListColors();
  const colorRules = colorConfig?.rules ?? [];

  // Phase 10d.3: Fetch all external views to show "shared" badges on project rows
  const { data: externalViewsData } = useQuery({
    queryKey: ['external-views'],
    queryFn: () => api.externalViews.list(),
    enabled: hasPortalManagement,
    staleTime: 60_000,
  });

  // Map: projectId → member count (from view panels)
  const sharedProjectInfo = useMemo((): Map<string, number> => {
    const map = new Map<string, number>();
    if (!externalViewsData?.views) return map;
    for (const view of externalViewsData.views) {
      if (!view.is_active) continue;
      for (const panel of (view.panels ?? [])) {
        const projectId = panel?.config?.project_id;
        if (projectId) {
          // Use member_count if available, otherwise mark as shared (1)
          map.set(projectId, (view.member_count ?? view.members_count ?? 1));
        }
      }
    }
    return map;
  }, [externalViewsData]);

  // Read filters from URL params
  const currentView = searchParams.get('view') || 'active-work';
  const proposalSubFilter = searchParams.get('proposal-filter') || 'active'; // active or archived
  const search = searchParams.get('search') || '';
  const categoryFilter = searchParams.get('category') || '';

  // Phase 7.5.13: Sort state from URL params - default depends on view
  // Proposals sort by updated_at, projects sort by project_number
  const defaultSort = currentView === 'proposals' ? 'updated_at' : 'project_number';
  const sortBy = searchParams.get('sort') || defaultSort;
  const sortOrder = searchParams.get('order') || 'desc';

  // Phase 6: Pagination state management in URL
  const { page, limit, offset, setPage, resetToFirstPage } = usePaginationState(50);

  // 9.15c: Store page before navigating to detail page
  const { storeCurrentPage } = useReferrerPage('projects');

  // Phase 6: Backend filtering replaces client-side 30-day split for proposals
  // Active proposals = draft/sent updated within 30 days
  // Archived proposals = declined OR (draft/sent older than 30 days) - mutually exclusive with Active
  const updatedWithinDays =
    currentView === 'proposals' && proposalSubFilter === 'active' ? 30 : undefined;
  const excludeActiveProposals =
    currentView === 'proposals' && proposalSubFilter === 'archived' ? true : undefined;

  // Phase 7.5.13: Map view to workflow stages
  // Proposals view:
  //   - Active filter: draft/sent only (+ 30 day time filter) - excludes declined
  //   - Archived filter: ALL proposals (draft/sent/declined) EXCEPT those in Active tab
  // Active work view: project_setup through construction_administration (excludes proposals, on_hold, and complete)
  // All Projects view: truly ALL projects (no workflow_stage filter - includes proposals, active work, archived)
  const getWorkflowStage = () => {
    if (currentView === 'proposals') {
      // Active = only draft/sent (not declined), within 30 days
      // Archived = ALL proposals including declined
      if (proposalSubFilter === 'active') {
        return 'proposal_draft,proposal_sent';
      }
      return 'proposal_draft,proposal_sent,proposal_declined';
    }
    if (currentView === 'active-work') {
      // Active work = projects in progress (not on hold, not complete)
      return 'project_setup,design_development,permit_preparation,in_permit_review,construction_administration';
    }
    if (currentView === 'all') {
      // All Projects = truly ALL projects including proposals (no workflow_stage filter)
      return undefined;
    }
    if (currentView === 'archived') {
      // Archived projects can have any workflow_stage
      return undefined;
    }
    // action-needed view uses has_action_needed filter, no workflow_stage filter
    return undefined;
  };

  // Fetch projects with backend filtering and pagination (Phase 6)
  const { data, isLoading, error } = useProjects({
    search: search || undefined,
    workflow_stage: getWorkflowStage(),
    has_action_needed: currentView === 'action-needed' ? true : undefined,
    updated_within_days: updatedWithinDays,
    exclude_active_proposals: excludeActiveProposals,
    is_archived: currentView === 'archived' ? true : undefined,
    // #150: Proposals → Archived surfaces user-archived proposals alongside
    // declined/stale ones, each with its own Restore/Archive row action.
    include_archived:
      (currentView === 'proposals' && proposalSubFilter === 'archived') ||
      // Christa 2026-09-04 (feedback b6c32231): searching All Projects finds archived ones too.
      (currentView === 'all' && !!search)
        ? true
        : undefined,
    category: categoryFilter || undefined,
    limit,
    offset,
    sort_by: sortBy,
    sort_order: sortOrder,
  }, {
    keepPreviousData: true, // Smooth page transitions
  });

  const projects = data?.projects || [];
  const total = data?.total || 0;

  // Christa 2026-09-04 (feedback b6c32231): she searched "A135" on the default Active Work tab, and A135 is a finished
  // project, so nothing showed. A search outside All Projects also counts the matches there (finished and archived
  // included). When that finds more than this tab shows, say how many more and offer to show them. Every tab is a
  // subset of that wider search, so the difference is what this tab hides.
  const searchOutsideAll =
    !!search && currentView !== 'all' && !isLoading && !error && data !== undefined;
  const { data: widerSearchData } = useProjects({
    search: search || undefined,
    include_archived: true,
    category: categoryFilter || undefined,
    limit: 1,
    offset: 0,
  }, {
    enabled: searchOutsideAll,
  });
  const widerMatchCount = searchOutsideAll ? Math.max((widerSearchData?.total ?? 0) - total, 0) : 0;

  // Calculate pagination values
  const paginationValues = calculatePaginationValues(total, offset, limit);

  // Phase 7.5.2: Project category filter options
  const { data: categoryOptions } = useProjectCategoryOptions();
  const categorySelectValue = categoryFilter || '__all__';
  const activeFilterCount = (search ? 1 : 0) + (categoryFilter ? 1 : 0);

  const handleCategoryFilterChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === '__all__') {
      newParams.delete('category');
      // Phase 9.6d FILTER-02: Clear tenant-scoped preference
      removeTenantItem('preferredCategory', tenantId);
    } else {
      newParams.set('category', value);
      // Phase 9.6d FILTER-02: Save tenant-scoped category preference
      setTenantItem('preferredCategory', value, tenantId);
    }
    // Reset to first page when category changes
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Phase 9.6d FILTER-02: Load tenant-scoped category preference on mount
  useEffect(() => {
    if (!tenantId) return;
    // Migrate any legacy un-prefixed key first
    migrateLegacyKey('preferredCategory', tenantId);
    // Only apply preference if no category in URL
    if (!searchParams.get('category')) {
      const savedCategory = getTenantItem('preferredCategory', tenantId);
      if (savedCategory) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('category', savedCategory);
        setSearchParams(newParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]); // Re-run when tenant changes (e.g. tenant switch)

  // Handle view tab change
  const handleViewChange = (view: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (view === 'active-work') {
      // Active Work is the default - remove view param for clean URL
      newParams.delete('view');
      newParams.delete('proposal-filter'); // Clear sub-filter
    } else {
      newParams.set('view', view);
      // Default to 'active' when switching to proposals
      if (view === 'proposals') {
        newParams.set('proposal-filter', 'active');
      } else {
        newParams.delete('proposal-filter');
      }
    }
    // Phase 6: Reset to page 1 when view changes
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle proposal sub-filter change (active/archived)
  const handleProposalSubFilterChange = (subFilter: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('proposal-filter', subFilter);
    // Phase 6: Reset to page 1 when sub-filter changes
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    // Phase 6: Reset to page 1 when search changes (combined with search update to avoid race condition)
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    // Clear all params (search, view, proposal-filter, page, etc.)
    setSearchParams(new URLSearchParams());
  };

  // Phase 7.5: Handle sort column click
  const handleSort = (column: string) => {
    const newParams = new URLSearchParams(searchParams);

    if (sortBy === column) {
      // Toggle order if same column
      newParams.set('order', sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc (highest first)
      newParams.set('sort', column);
      newParams.set('order', 'desc');
    }

    // Reset to first page when sort changes
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Helper to format relative time
  const formatRelativeTime = (dateString: string): string => {
    // Append 'Z' if no timezone info to treat as UTC
    const normalizedDate = dateString.includes('Z') || dateString.includes('+')
      ? dateString
      : dateString + 'Z';

    const date = new Date(normalizedDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // Handle future dates or invalid dates
    if (diffMs < 0 || isNaN(diffMs)) {
      return date.toLocaleDateString();
    }

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const updateStageMutation = useUpdateProject();

  const isProposalStage = (stage: string | null | undefined): boolean =>
    ['proposal_draft', 'proposal_sent', 'proposal_declined'].includes(stage ?? '');

  const handleStageUpdate = (projectId: string, newStage: string) => {
    updateStageMutation.mutate({ id: projectId, data: { workflow_stage: newStage as WorkflowStage } });
  };

  // Phase 7.5.14: Column Configuration System — now the shared DataTable
  // column config. `responsive` hides lower-priority columns below the given
  // breakpoint; `wrap` + `maxWidth` let long text take a second line instead
  // of forcing a wide single-line column; Number + Name freeze at lg+ during
  // horizontal scroll (mirrors the invoices checkbox + invoice # pattern).
  const columns = useMemo((): DataTableColumn<Project>[] => {
    const isProposalsView = currentView === 'proposals';

    const baseColumns: DataTableColumn<Project>[] = [
      // Number column - only for projects
      ...(!isProposalsView ? [{
        key: 'project_number',
        label: 'Number',
        sortable: true,
        sortKey: 'project_number',
        width: 'w-[100px]',
        responsive: 'lg',
        sticky: true,
        stickyLeft: 'lg:left-0',
        render: (p: Project) => (
          <span className="font-mono">{p.project_number}</span>
        ),
      } satisfies DataTableColumn<Project>] : []),

      // Name - all views; frozen at lg+ so identity stays visible while scrolling
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        sortKey: 'name',
        sticky: true,
        stickyLeft: isProposalsView ? 'lg:left-0' : 'lg:left-[100px]',
        wrap: true,
        maxWidth: 'max-w-[240px]',
        render: (p: Project) => {
          const viewerCount = sharedProjectInfo.get(p.id);
          return (
            <div className="flex items-center gap-1.5">
              <span className="font-medium">{p.name}</span>
              {/* Phase 10d.3: Shared badge */}
              {viewerCount !== undefined && (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 border border-blue-200 bg-blue-50 rounded px-1 py-0 shrink-0"
                  title={`${viewerCount} external viewer${viewerCount !== 1 ? 's' : ''}`}
                  data-testid={`shared-badge-${p.id}`}
                >
                  <Share2 className="h-2.5 w-2.5" />
                  {viewerCount}
                </span>
              )}
            </div>
          );
        },
      },

      // Address - all views; wraps to a second line instead of widening the table
      {
        key: 'address',
        label: 'Address',
        responsive: 'xl',
        wrap: true,
        maxWidth: 'max-w-[220px]',
        render: (p: Project) => (
          <span className="text-muted-foreground">{p.address || '—'}</span>
        ),
      },

      // Primary Client - all views (Phase 9.7bc)
      {
        key: 'client',
        label: 'Primary Client',
        tooltip: 'Shows only the primary client for projects with multiple clients',
        responsive: 'lg',
        wrap: true,
        maxWidth: 'max-w-[180px]',
        render: (p: Project) => (
          <span className="text-muted-foreground">{p.client_name || '-'}</span>
        ),
      },

      // Category - all views
      {
        key: 'category',
        label: 'Category',
        responsive: 'lg',
        render: (p: Project) => p.category ? (
          <Badge variant="outline">{p.category}</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      },
    ];

    // Financial columns - different for proposals vs projects
    const financialColumns: DataTableColumn<Project>[] = isProposalsView ? [
      // Proposals: Proposed (Required)
      {
        key: 'proposed_required',
        label: 'Proposed (Required)',
        sortable: true,
        sortKey: 'proposed_required_total',
        align: 'right',
        render: (p: Project) => (
          <span className="font-mono">{formatCurrency(p.proposed_required_total)}</span>
        ),
      },

      // Proposals: Optional (calculated, not sortable)
      {
        key: 'optional',
        label: 'Optional',
        align: 'right',
        responsive: 'lg',
        render: (p: Project) => {
          const optional = (p.proposed_total ?? 0) - (p.proposed_required_total ?? 0);
          return <span className="font-mono">{formatCurrency(optional)}</span>;
        },
      },

      // Proposals: Sent Date
      {
        key: 'proposal_sent_at',
        label: 'Sent Date',
        sortable: true,
        sortKey: 'proposal_sent_at',
        responsive: 'md',
        render: (p: Project) => (
          <span className="text-muted-foreground text-sm">
            {p.proposal_sent_at ? new Date(p.proposal_sent_at).toLocaleDateString() : '—'}
          </span>
        ),
      },
    ] : [
      // Projects: Confirmed
      {
        key: 'confirmed',
        label: 'Confirmed',
        sortable: true,
        sortKey: 'confirmed_total',
        align: 'right',
        responsive: 'xl',
        render: (p: Project) => (
          <span className="font-mono">{formatCurrency(p.confirmed_total)}</span>
        ),
      },

      // Projects: Invoiced
      {
        key: 'invoiced',
        label: 'Invoiced',
        sortable: true,
        sortKey: 'invoiced_total',
        align: 'right',
        render: (p: Project) => (
          <span className="font-mono">{formatCurrency(p.invoiced_total)}</span>
        ),
      },

      // Projects: Paid
      {
        key: 'paid',
        label: 'Paid',
        sortable: true,
        sortKey: 'paid_total',
        align: 'right',
        responsive: 'lg',
        render: (p: Project) => (
          <span className="font-mono">{formatCurrency(p.paid_total)}</span>
        ),
      },
    ];

    const endColumns: DataTableColumn<Project>[] = [
      // Workflow Stage - all views
      {
        key: 'workflow_stage',
        label: 'Stage',
        render: (p: Project) => {
          if (!p.workflow_stage) return '—';
          if (isProposalStage(p.workflow_stage)) {
            return (
              <ProjectStageBadge workflowStage={p.workflow_stage as WorkflowStage} updatedAt={p.updated_at} />
            );
          }
          return (
            <InlineStageSelect
              projectId={p.id}
              currentStage={p.workflow_stage as WorkflowStage}
              onUpdate={(newStage) => handleStageUpdate(p.id, newStage)}
            />
          );
        },
      },

      // Updated - all views
      {
        key: 'updated_at',
        label: 'Updated',
        sortable: true,
        sortKey: 'updated_at',
        responsive: 'md',
        render: (p: Project) => (
          <span className="text-muted-foreground text-sm">
            {p.updated_at ? formatRelativeTime(p.updated_at) : '—'}
          </span>
        ),
      },
    ];

    return [...baseColumns, ...financialColumns, ...endColumns];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, sharedProjectInfo]); // Recalculate when view or shared state changes

  // Phase 6: Handle page overflow - redirect to last valid page if current page exceeds total
  useEffect(() => {
    if (!isLoading && data && paginationValues.totalPages > 0) {
      if (page > paginationValues.totalPages) {
        setPage(paginationValues.totalPages);
      }
    }
  }, [page, paginationValues.totalPages, isLoading, data, setPage]);

  // Phase 6: Use delete hook with optimistic updates and total count handling
  const deleteMutation = useDeleteProject();
  const restoreMutation = useRestoreProject();


  const isArchivedView = currentView === 'archived';

  const handleDeleteClick = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectToDelete(project);
  };

  const entityLabel = (project: Project): string =>
    project.is_template ? 'Template' : isProposalStage(project.workflow_stage) ? 'Proposal' : 'Project';

  const handleRestoreClick = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    restoreMutation.mutate({ id: project.id, entityLabel: entityLabel(project) });
  };

  const confirmDelete = () => {
    if (projectToDelete) {
      deleteMutation.mutate(projectToDelete.id);
      setProjectToDelete(null); // Clear dialog state
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Projects">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout
        title="Projects"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTemplateModalOpen(true)}>
              <FileStack className="mr-2 h-4 w-4" />
              Start from Template
            </Button>
            <Button onClick={() => navigate('/projects/new')}>
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
          </div>
        }
      >
        <div className="mb-4">
          <p className="text-muted-foreground">
            Manage your projects and track billable work
          </p>
        </div>

        {/* View Tabs */}
        <ViewTabs
          views={projectViews}
          currentView={currentView}
          onChange={handleViewChange}
        />

        {/* Phase 9.6d FILTER-01: Category Tabs */}
        {categoryOptions && categoryOptions.length > 0 && (
          <div className="flex gap-1 mb-4 flex-wrap" role="tablist" aria-label="Category filter">
            <Button
              variant={!categoryFilter ? 'default' : 'outline'}
              size="sm"
              role="tab"
              aria-selected={!categoryFilter}
              onClick={() => handleCategoryFilterChange('__all__')}
            >
              All Categories
            </Button>
            {categoryOptions.map((category) => (
              <Button
                key={category.id}
                variant={categoryFilter === category.value ? 'default' : 'outline'}
                size="sm"
                role="tab"
                aria-selected={categoryFilter === category.value}
                onClick={() => handleCategoryFilterChange(category.value)}
              >
                {category.value}
              </Button>
            ))}
          </div>
        )}

        {/* Proposals Sub-Filters (Phase 5 Batch 3) */}
        {currentView === 'proposals' && (
          <div className="flex gap-2 mb-4">
            <Button
              variant={proposalSubFilter === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleProposalSubFilterChange('active')}
              title="Draft and sent proposals updated in the last 30 days"
            >
              Active
            </Button>
            <Button
              variant={proposalSubFilter === 'archived' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleProposalSubFilterChange('archived')}
              title="Declined proposals and proposals not updated in 30+ days"
            >
              Archived
            </Button>
          </div>
        )}

        {/* Filter Bar */}
        <FilterBar
          search={search}
          onSearchChange={handleSearchChange}
          placeholder="Search projects..."
          onClearAll={handleClearAll}
          activeFilterCount={activeFilterCount}
          advancedFilters={
            <Select value={categorySelectValue} onValueChange={handleCategoryFilterChange}>
              <SelectTrigger className="w-[180px]" aria-label="Category filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {(categoryOptions ?? []).map((option) => (
                  <SelectItem key={option.id} value={option.value}>
                    {option.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load projects: {(error as Error).message}
          </p>
        </div>
      )}

      {!error && total > 0 && widerMatchCount > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground"
          data-testid="search-more-in-all"
        >
          <span>
            {widerMatchCount} more in All Projects, including finished and archived ones.
          </span>
          <Button variant="outline" size="sm" onClick={() => handleViewChange('all')} data-testid="search-show-more-in-all">
            Show all matches
          </Button>
        </div>
      )}

      {/* Table — desktop only (md+); mobile gets the card stack below.
          Shared DataTable: breakpoint hiding + wrap columns + frozen Number/Name
          + sticky mirror scrollbar (same machinery as the invoices list). */}
      {!error && !isMobile && (
        <DataTable<Project>
          columns={[
            ...columns,
            // Actions column — per-row so archived rows offer Restore even
            // when the view itself isn't the dedicated Archived tab (#150).
            {
              key: 'actions',
              label: '',
              width: 'w-24',
              render: (project) =>
                project.is_archived ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleRestoreClick(project, e)}
                    disabled={restoreMutation.isPending}
                    className="text-primary hover:text-primary"
                    title="Restore"
                    aria-label="Restore"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteClick(project, e)}
                    disabled={deleteMutation.isPending}
                    className="text-muted-foreground hover:text-destructive"
                    title="Archive"
                    aria-label="Archive"
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                ),
            },
          ]}
          rows={projects}
          rowKey={(p) => p.id}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onRowClick={(project) => {
            storeCurrentPage(page); // 9.15c: Store page for back navigation
            navigate(`/projects/${project.id}`);
          }}
          // #150: dim per-row, so the Proposals → Archived view (mixed
          // archived + active rows) only fades the archived ones.
          // R2: border_left + row_tint appended last — inert when no rule matches.
          rowClassName={(project) =>
            cn(
              project.is_archived && 'opacity-60',
              rowTreatmentClasses(evaluateProjectColors(project, colorRules))
            )
          }
          // R2: dot/badge treatments render inline in the first cell.
          firstCellWrapper={(project, content) => {
            const treatment = evaluateProjectColors(project, colorRules);
            return (
              <span className="inline-flex items-center gap-1.5">
                <ProjectColorDot treatment={treatment} label={project.name} />
                <span>{content}</span>
                <ProjectColorBadge treatment={treatment} label={project.category ?? undefined} />
              </span>
            );
          }}
          emptyState={
            search ? (
              <SearchEmptyState
                noun={currentView === 'proposals' ? 'proposals' : 'projects'}
                widerMatchCount={widerMatchCount}
                onShowAll={() => handleViewChange('all')}
              />
            ) : currentView === 'proposals' ? (
              <EmptyState
                icon={FileText}
                title="No proposals yet"
                description="Proposals turn into projects when your client says yes"
                actionLabel="New Proposal"
                actionUrl="/projects/new"
              />
            ) : (
              <EmptyState
                icon={FolderOpen}
                title="No projects yet"
                description={
                  firmType === 'architect'
                    ? 'Create your first proposal to get started'
                    : firmType === 'general_contractor'
                    ? 'Create a project to start tracking field work'
                    : 'Create your first project'
                }
                actionLabel={firmType === 'architect' ? 'New Proposal' : 'New Project'}
                actionUrl="/projects/new"
              />
            )
          }
          containerTestId="projects-table"
        />
      )}

      {/* Mobile card stack — replaces the table at < md so all key fields stay visible */}
      {!error && isMobile && projects.length > 0 && (
        <ProjectMobileCardList
          projects={projects}
          view={currentView}
          isArchived={isArchivedView}
          colorRules={colorRules}
          onSelect={(project) => {
            storeCurrentPage(page);
            navigate(`/projects/${project.id}`);
          }}
          formatRelativeTime={formatRelativeTime}
        />
      )}

      {/* Mobile empty state — table's empty cell is hidden at < md */}
      {!error && isMobile && projects.length === 0 && (
        <div className="rounded-md border bg-white" data-testid="projects-mobile-empty">
          {search ? (
            <SearchEmptyState
              noun={currentView === 'proposals' ? 'proposals' : 'projects'}
              widerMatchCount={widerMatchCount}
              onShowAll={() => handleViewChange('all')}
            />
          ) : currentView === 'proposals' ? (
            <EmptyState
              icon={FileText}
              title="No proposals yet"
              description="Proposals turn into projects when your client says yes"
              actionLabel="New Proposal"
              actionUrl="/projects/new"
            />
          ) : (
            <EmptyState
              icon={FolderOpen}
              title="No projects yet"
              description={
                firmType === 'architect'
                  ? 'Create your first proposal to get started'
                  : firmType === 'general_contractor'
                  ? 'Create a project to start tracking field work'
                  : 'Create your first project'
              }
              actionLabel={firmType === 'architect' ? 'New Proposal' : 'New Project'}
              actionUrl="/projects/new"
            />
          )}
        </div>
      )}

      {/* Phase 6: Pagination Footer */}
      {!error && total > 0 && (
        <div className="flex items-center justify-between">
          <PaginationInfo
            total={total}
            start={paginationValues.start}
            end={paginationValues.end}
            itemName="projects"
          />

          {paginationValues.totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage(page - 1)}
                    className={!paginationValues.hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>

                {/* First page */}
                {paginationValues.currentPage > 2 && (
                  <PaginationItem>
                    <PaginationLink onClick={() => setPage(1)} className="cursor-pointer">
                      1
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Left ellipsis */}
                {paginationValues.currentPage > 3 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Previous page */}
                {paginationValues.hasPreviousPage && (
                  <PaginationItem>
                    <PaginationLink onClick={() => setPage(page - 1)} className="cursor-pointer">
                      {page - 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Current page */}
                <PaginationItem>
                  <PaginationLink isActive className="cursor-default">
                    {page}
                  </PaginationLink>
                </PaginationItem>

                {/* Next page */}
                {paginationValues.hasNextPage && (
                  <PaginationItem>
                    <PaginationLink onClick={() => setPage(page + 1)} className="cursor-pointer">
                      {page + 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Right ellipsis */}
                {paginationValues.currentPage < paginationValues.totalPages - 2 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Last page */}
                {paginationValues.currentPage < paginationValues.totalPages - 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => setPage(paginationValues.totalPages)}
                      className="cursor-pointer"
                    >
                      {paginationValues.totalPages}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage(page + 1)}
                    className={!paginationValues.hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
    </DashboardLayout>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={!!projectToDelete} onOpenChange={() => setProjectToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-muted-foreground" />
              Archive Project
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to archive "{projectToDelete?.name}"?</p>
              <p>
                The project and all its data will be preserved but hidden from active views. You can restore it at any time from the Archived tab.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
            >
              {deleteMutation.isPending ? 'Archiving...' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Selection Modal - Phase 7 Batch 7.3 */}
      <TemplateSelectionModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
      />
    </>
  );
}

/**
 * Empty result for a search. When the same search matches projects outside this tab (finished or archived ones), say
 * how many and offer to show them in All Projects.
 */
function SearchEmptyState({
  noun,
  widerMatchCount,
  onShowAll,
}: {
  noun: 'projects' | 'proposals';
  widerMatchCount: number;
  onShowAll: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground" data-testid="search-empty-state">
      <p>No {noun} found on this tab</p>
      {widerMatchCount > 0 ? (
        <>
          <p className="text-sm mt-1" data-testid="search-wider-matches">
            {widerMatchCount === 1
              ? '1 match in All Projects, including finished and archived ones.'
              : `${widerMatchCount} matches in All Projects, including finished and archived ones.`}
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={onShowAll} data-testid="search-show-all-projects">
            Show {widerMatchCount === 1 ? 'it' : 'them'}
          </Button>
        </>
      ) : (
        <p className="text-sm mt-1">Try adjusting your search or filters</p>
      )}
    </div>
  );
}
